import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { settingsApi } from '../../../../../api/settings/settingsApi';
import { ISingleValueSelect } from '../../../../../utils/interfaces';
import { IUser, IUserFilter } from '../interfaces/IUser';

export const fetchUserList = createAsyncThunk(
    'fetchUserList',
    async (
        {
            tenantId,
            userName,
            userStatus,
            role,
        }: {
            tenantId: string;
            userName?: string;
            userStatus?: boolean;
            role?: string;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await settingsApi.fetchUsersList(
                tenantId,
                userName,
                userStatus,
                role,
            );
            return response ?? [];
        } catch {
            return rejectWithValue('Error');
        }
    },
);

export const SELECTED_INITIAL_STATE: IUser = {
    firstName: '',
    lastName: '',
    contactPhone: '',
    username: '',
    roles: [
        {
            roleId: 0,
            name: 'Admin',
        },
    ],
    commissionInUSD: null,
    enabled: true,
    verified: false,
};

export type IUserInitialState = {
    usersListLoading: boolean;
    usersList: IUser[];
    selectedUser: IUser;
    selectedUserRole: ISingleValueSelect;
    selectedUserStatus: ISingleValueSelect;
    userFilter: IUserFilter;
};

const initialUserState: IUserInitialState = {
    usersListLoading: false,
    usersList: [],
    selectedUser: SELECTED_INITIAL_STATE,
    selectedUserRole: {
        value: '',
        label: 'Select Role',
    },
    selectedUserStatus: {
        value: '',
        label: '',
    },
    userFilter: {
        role: {
            value: '',
            label: 'Select Role',
        },
        userStatus: {
            value: '',
            label: 'Status',
        },
        userName: '',
    },
};

export const userSlice = createSlice({
    name: 'userDetails',
    initialState: initialUserState,
    reducers: {
        setUserList: (state, action: PayloadAction<IUser[]>) => {
            state.usersList = action.payload;
        },
        setSelectedUser: (state, action: PayloadAction<IUser>) => {
            state.selectedUser = action.payload;
        },
        setSelectedUserRole: (
            state,
            action: PayloadAction<ISingleValueSelect>,
        ) => {
            state.selectedUserRole = action.payload;
        },
        setSelectedUserStatus: (
            state,
            action: PayloadAction<ISingleValueSelect>,
        ) => {
            state.selectedUserStatus = action.payload;
        },
        setUserFilter: (state, action: PayloadAction<IUserFilter>) => {
            state.userFilter = action.payload;
        },
        clearSelectedUser: state => {
            state.selectedUser = SELECTED_INITIAL_STATE;
            state.selectedUserRole = {
                value: '',
                label: 'Select Role',
            };
            state.selectedUserStatus = {
                value: '',
                label: '',
            };
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchUserList.pending, state => {
            state.usersListLoading = true;
            state.usersList = [];
        });
        builder.addCase(fetchUserList.fulfilled, (state, action) => {
            state.usersListLoading = false;
            state.usersList = action.payload;
        });
        builder.addCase(fetchUserList.rejected, state => {
            state.usersListLoading = false;
            state.usersList = [SELECTED_INITIAL_STATE];
        });
    },
});

export const {
    setUserList,
    setSelectedUser,
    clearSelectedUser,
    setSelectedUserRole,
    setSelectedUserStatus,
    setUserFilter,
} = userSlice.actions;

const UserReducer = userSlice.reducer;
export default UserReducer;
